import type { CartSnapshot, PagePlatform, PageType } from '@teo/widget-shared';
import { CART_SOURCE, type CartPartial } from './cart-partial.js';
import { collectCartPageDomPartial } from './collect-cart-page-dom.js';
import { collectGtmCartPartial } from './collect-gtm-cart.js';
import { collectHeaderCartCountPartial } from './collect-header-badge.js';
import {
  collectShopifyCartPartial,
  type FetchShopifyCartOptions,
} from './collect-shopify-cart.js';
import { resolveCartSnapshot } from './resolve-cart-snapshot.js';

export type CollectCartSnapshotOptions = {
  doc?: Document;
  pageType?: PageType;
  platform?: PagePlatform;
  /** Skip network Shopify fetch (unit tests / sync path). */
  skipShopifyFetch?: boolean;
  shopify?: FetchShopifyCartOptions;
  dataLayer?: unknown[];
};

function orderPartials(partials: CartPartial[]): CartPartial[] {
  const bySource = new Map(partials.map((row) => [row.source, row]));
  return [
    bySource.get(CART_SOURCE.gtm),
    bySource.get(CART_SOURCE.shopify),
    bySource.get(CART_SOURCE.cartPageDom),
    bySource.get(CART_SOURCE.headerBadge),
  ].filter((row): row is CartPartial => row != null);
}

function isShopifyHost(platform?: PagePlatform): boolean {
  if (platform === 'shopify') return true;
  return typeof window !== 'undefined' && Boolean(window.Shopify);
}

/**
 * Sync collectors only (GTM + DOM). Shopify network fetch is async — use
 * `collectCartSnapshotAsync` when platform may be Shopify.
 */
export function collectCartSnapshotSync(
  options: CollectCartSnapshotOptions = {},
): CartSnapshot | null {
  const doc = options.doc ?? (typeof document !== 'undefined' ? document : undefined);
  if (!doc) {
    const partial = collectGtmCartPartial(options.dataLayer);
    return partial ? resolveCartSnapshot([partial]) : null;
  }

  return resolveCartSnapshot(
    orderPartials(
      [
        collectGtmCartPartial(options.dataLayer),
        options.pageType === 'cart' ? collectCartPageDomPartial(doc) : null,
        collectHeaderCartCountPartial(doc),
      ].filter((row): row is CartPartial => row != null),
    ),
  );
}

/** Full cart snapshot including optional Shopify `/cart.js`. */
export async function collectCartSnapshotAsync(
  options: CollectCartSnapshotOptions = {},
): Promise<CartSnapshot | null> {
  const doc = options.doc ?? (typeof document !== 'undefined' ? document : undefined);
  const partials: CartPartial[] = [
    collectGtmCartPartial(options.dataLayer),
    options.pageType === 'cart' && doc ? collectCartPageDomPartial(doc) : null,
    doc ? collectHeaderCartCountPartial(doc) : null,
  ].filter((row): row is CartPartial => row != null);

  if (!options.skipShopifyFetch && isShopifyHost(options.platform)) {
    const shopify = await collectShopifyCartPartial(options.shopify);
    if (shopify) partials.push(shopify);
  }

  return resolveCartSnapshot(orderPartials(partials));
}
